import { computed, inject, Injectable } from "@angular/core";
import { AuthService } from "./auth.service";

export enum StaffFeature {
  Dashboard = "Dashboard",
  RegistrationPlot = "RegistrationPlot",
  Participants = "Participants",
  ParticipantDetail = "ParticipantDetail",
  ParticipantScanner = "ParticipantScanner",
  Orders = "Orders",
  Vouchers = "Vouchers",
  VoucherList = "VoucherList",
  VoucherScanner = "VoucherScanner",
  Users = "Users",
  LoyaltyCustomers = "LoyaltyCustomers",
  Events = "Events",
  EventSessions = "EventSessions",
  Notifications = "Notifications",
  Settings = "Settings",
  Maintenance = "Maintenance",
  AIIntegrations = "AIIntegrations",
  SeatingPreview = "SeatingPreview",
  SessionReservations = "SessionReservations",
  Reports = "Reports",
  Impersonation = "Impersonation",
  Site = "Site",
}

/**
 * Maps each feature to the groups that grant access.
 * An empty array means superuser-only.
 */
const FEATURE_GROUPS: Record<StaffFeature, string[]> = {
  [StaffFeature.Dashboard]: ["Registration"],
  [StaffFeature.RegistrationPlot]: ["Registration"],
  [StaffFeature.Participants]: ["Registration"],
  [StaffFeature.ParticipantDetail]: ["Registration", "Access"],
  [StaffFeature.ParticipantScanner]: ["Registration", "Access"],
  [StaffFeature.Orders]: ["Registration", "Shop"],
  [StaffFeature.Vouchers]: ["Registration", "Shop"],
  [StaffFeature.VoucherList]: [],
  [StaffFeature.VoucherScanner]: ["Registration", "Shop"],
  [StaffFeature.Users]: [],
  // Cross-system loyalty report (PII: emails/phones). Same bar as the staff
  // participant lists — Registration group + superusers (backend endpoints
  // are gated by IsRegistrationGroup).
  [StaffFeature.LoyaltyCustomers]: ["Registration"],
  [StaffFeature.Events]: [],
  [StaffFeature.EventSessions]: ["Registration", "Access"],
  [StaffFeature.Notifications]: [],
  [StaffFeature.Settings]: [],
  [StaffFeature.Maintenance]: [],
  [StaffFeature.AIIntegrations]: [],
  // Table-seating preview (clusters of who-wants-with-whom): same gating
  // as the events table — Registration group + superusers.
  [StaffFeature.SeatingPreview]: ["Registration"],
  // Staff-driven manual reservations on a session (gratis order).
  [StaffFeature.SessionReservations]: ["Registration"],
  // Financial revenue report — superuser-only (backend endpoint is IsSuperUser).
  [StaffFeature.Reports]: [],
  // Impersonation audit log — superuser-only (backend endpoint is IsSuperUser).
  [StaffFeature.Impersonation]: [],
  // Modulul „Site” (CMS www, P1-C) — doar superuser (API-ul /api/v1/www/ e IsSuperUser).
  [StaffFeature.Site]: [],
};

@Injectable({
  providedIn: "root",
})
export class PermissionService {
  private readonly authService = inject(AuthService);
  private readonly featureCache = new Map<StaffFeature, ReturnType<typeof computed<boolean>>>();

  readonly isSuperuser = computed(() => {
    return this.authService.currentUser()?.is_superuser ?? false;
  });

  readonly userGroups = computed(() => {
    return this.authService.currentUser()?.groups ?? [];
  });

  hasGroup(group: string) {
    return computed(() => {
      if (this.isSuperuser()) return true;
      return this.userGroups().includes(group);
    });
  }

  hasAnyGroup(...groups: string[]) {
    return computed(() => {
      if (this.isSuperuser()) return true;
      const userGroups = this.userGroups();
      return groups.some((g) => userGroups.includes(g));
    });
  }

  canAccess(feature: StaffFeature) {
    let cached = this.featureCache.get(feature);
    if (!cached) {
      cached = computed(() => {
        if (this.isSuperuser()) return true;
        const allowedGroups = FEATURE_GROUPS[feature];
        if (allowedGroups.length === 0) return false; // superuser-only
        const userGroups = this.userGroups();
        return allowedGroups.some((g) => userGroups.includes(g));
      });
      this.featureCache.set(feature, cached);
    }
    return cached;
  }

  /**
   * True if user is in Access group but NOT in Registration group.
   * Used to show limited participant info.
   */
  readonly isAccessOnly = computed(() => {
    if (this.isSuperuser()) return false;
    const groups = this.userGroups();
    return groups.includes("Access") && !groups.includes("Registration");
  });
}
